/**
 * Окно замечания к «Юниверситету» (16.10.2026, владелец: «в открывшемся поле смогли написать свои замечания на
 * исправления»). Сверху — к чему замечание (путь по экранам и текст элемента), ниже поле; наша клавиатура выезжает
 * сразу (docked TextPad, шит поднимается на её высоту — как в отчёте о доп.работе). Случаи:
 *  • двойное «Отправить» — sending-ref + busy; ключ держится за текстом: повтор после обрыва тем же текстом —
 *    тот же ключ (не задвоит, даже если окно закрыли и открыли снова — ключ хранит вкладка, onPending);
 *    текст поправили — новый ключ (лучше возможный дубль, чем потерянная правка; ревью 16.10);
 *  • нет связи — текст остаётся в окне, «Отправить» ещё раз безопасно;
 *  • закрыли, не отправив, — черновик живёт у вкладки (onDraft) и вернётся, если снова зажать то же место.
 */
import { useEffect, useRef, useState } from "react";
import { SheetDialog, TextPad } from "../../../../components/shared";
import { blurOtherField } from "../../../../components/shared/padFocus";
import { newRequestId } from "../../../../hooks/payoutsApi";
import { sendFeedback, type FbScope, type FbTarget } from "./fbApi";
import d from "../../dashActions.module.css";
import css from "./feedback.module.css";

interface Props {
  target: FbTarget | null;
  scope: FbScope;
  draft: string;
  onDraft: (text: string) => void;
  /** ключ отправки с неизвестным исходом и текст, с которым он ушёл (живёт у вкладки, переживает закрытие окна) */
  pending: Pending | null;
  onPending: (p: Pending | null) => void;
  onClose: () => void;
  onSent: () => void;
}

export type Pending = { key: string; text: string };

export function FeedbackSheet({ target, scope, draft, onDraft, pending, onPending, onClose, onSent }: Props) {
  const open = !!target;
  const [text, setText] = useState("");
  const [pad, setPad] = useState(false);
  const [dockH, setDockH] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const textRef = useRef<HTMLTextAreaElement>(null);
  const reqId = useRef<Pending | null>(null);   // ключ и текст, с которым он однажды ушёл без ответа
  const sending = useRef(false);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  /* новое открытие — свой ключ, черновик этого места и сразу клавиатура */
  useEffect(() => {
    if (!target) return;
    reqId.current = pending;
    sending.current = false;
    setText(draft); setBusy(false); setErr(null); setDockH(0);
    const t = window.setTimeout(() => { blurOtherField(textRef.current); setPad(true); }, 260);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  useEffect(() => {
    if (!dockH) return;
    const t = window.setTimeout(() => textRef.current?.scrollIntoView({ block: "center", behavior: "smooth" }), 220);
    return () => window.clearTimeout(t);
  }, [dockH]);

  const put = (v: string) => { setText(v); onDraft(v); };
  const dismiss = () => { if (!busy) { setPad(false); onClose(); } };
  const send = async () => {
    const comment = text.trim();
    if (!target || !comment || sending.current) return;
    sending.current = true;
    setBusy(true); setErr(null); setPad(false);
    const key = reqId.current && reqId.current.text === comment ? reqId.current.key : newRequestId();
    const r = await sendFeedback(scope, target, comment, key);
    sending.current = false;
    /* исход неизвестен — ключ с этим текстом бережём (у вкладки — и после закрытия окна); известен — отпускаем */
    reqId.current = !r.ok && r.unknown ? { key, text: comment } : null;
    onPending(reqId.current);
    if (!alive.current) return;
    setBusy(false);
    if (r.ok) { onDraft(""); onSent(); return; }
    setErr(r.error);
  };

  const what = target ? [target.part, target.media ? "файл пакета" : ""].filter(Boolean).join(" · ") : "";
  return (
    <SheetDialog open={open} onDismiss={dismiss} ariaLabel="Замечание" maxHeight="80dvh" liftBy={dockH}>
      <div className={d.sheetHead} data-fb-skip="">
        <span className={`${d.sheetTitle} ${css.title}`}>Замечание</span>
        {what ? <span className={d.sheetDate}>{what}</span> : null}
      </div>
      <div className={d.sheetBody} data-fb-skip="">
        <div className={css.place}>{target?.place || "Юниверситет"}</div>
        {target?.quote ? <blockquote className={css.quote}>{target.quote}</blockquote> : null}
        <div className={d.hint}>Что не так и как должно быть</div>
        <textarea className={d.textarea} ref={textRef} value={text} inputMode="none" readOnly={!pad} data-pad-host=""
          placeholder="Напр.: опечатка в ответе, должно быть «влево»; видео не грузится; непонятный вопрос…"
          onChange={(e) => put(e.target.value)}
          onFocus={() => { blurOtherField(textRef.current); setPad(true); }}
          onPointerDown={() => setPad(true)} />
        {err ? <div className={d.err} role="alert">{err}</div> : null}
        <TextPad open={pad && open} docked hostRef={textRef} multiline numRow label="Замечание" maxLength={1000}
          value={text} onChange={put} onDockHeight={setDockH} onClose={() => setPad(false)} />
      </div>
      <div className={d.foot} data-fb-skip="">
        <button type="button" className={`${d.btn} ${d.btnCancel}`} onClick={dismiss} disabled={busy}>Отмена</button>
        <button type="button" className={`${d.btn} ${d.btnGo}`} onClick={() => void send()} disabled={busy || !text.trim()}>
          {busy ? "Отправляю…" : "Отправить"}
        </button>
      </div>
    </SheetDialog>
  );
}
